import { FormContent } from "@/components/page-canvas";
import { Form, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import InputError from "@/components/ui/input-error";
import { FormErrors } from "@/components/forms/form-errors";
import { ConfirmSubmitButton } from "@/components/confirm-submit-button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { locationJobProgressPath, locationJobActivityPath } from "@/routes";
import { dateTime, money, type Job } from "./types";

export default function JobProgress({
  job,
  billingStaff,
  billingAssigneeName,
  mode = "overview",
}: {
  job: Job;
  billingStaff: { id: string; name: string }[];
  billingAssigneeName: string | null;
  mode?: "overview" | "billing";
}) {
  const {
    url,
    props: { auth, errors, i18n },
  } = usePage();
  const { t } = useTranslation();
  if (!auth.location) return null;
  const locationId = auth.location.id;
  const total = job.representation.total;
  const options = (
    name: string,
    label: string,
    items: Record<string, string>,
  ) => (
    <div className="space-y-2">
      <Label htmlFor={name}>{label}</Label>
      <Select
        name={`progress[${name.replaceAll("-", "_")}]`}
        items={items}
        required
      >
        <SelectTrigger
          id={name}
          className="w-full"
          aria-invalid={!!errors[name.replaceAll("-", "_")]}
        >
          <SelectValue placeholder={t("jobs.choose")} />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(items).map(([value, title]) => (
            <SelectItem key={value} value={value}>
              {title}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <InputError message={errors[name.replaceAll("-", "_")]} />
    </div>
  );
  const form = (action: string, label: string, fields?: ReactNode) => (
    <FormContent>
      <Form
        action={locationJobProgressPath(locationId, job.id)}
        method="patch"
        className="space-y-4"
      >
        {({ processing }) => (
          <>
            <input type="hidden" name="progress[action]" value={action} />
            <input
              type="hidden"
              name="return_section"
              value={
                new URLSearchParams(url.split("?")[1]).get("section") ||
                "overview"
              }
            />
            <input
              type="hidden"
              name="progress[revision]"
              value={job.revision}
            />
            {fields}
            {action === "cancel" ? (
              <ConfirmSubmitButton
                title={t("record_management.job_cancel_confirm")}
                description={t("record_management.job_cancel_confirm_help")}
                confirmLabel={label}
                cancelLabel={t("customers.cancel")}
                processing={processing}
                details={(values) => (
                  <dl className="space-y-3 text-sm">
                    <div>
                      <dt className="text-muted-foreground">
                        {t("job_board.cancel_type")}
                      </dt>
                      <dd className="font-medium">
                        {t(
                          `job_board.stages.${values.get("progress[no_show]") === "true" ? "no_show" : "cancelled"}`,
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">
                        {t("job_board.reason")}
                      </dt>
                      <dd className="whitespace-pre-wrap break-words">
                        {String(values.get("progress[reason]") || "")}
                      </dd>
                    </div>
                  </dl>
                )}
              >
                {label}
              </ConfirmSubmitButton>
            ) : (
              <Button type="submit" disabled={processing}>
                {label}
              </Button>
            )}
          </>
        )}
      </Form>
    </FormContent>
  );
  if (mode === "billing")
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("product_ux_staff.billing_responsibility")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <FormErrors
            errors={errors || {}}
            targets={{ billing_assignee: "billing-assignee-id" }}
          />
          <p>
            {billingAssigneeName ||
              t("product_ux_staff.billing_assignment_needed")}
          </p>
          <p className="text-muted-foreground text-sm">
            {t("product_ux_staff.billing_help")}
          </p>
          {job.work_state === "billing" &&
            billingStaff.length > 0 &&
            form(
              "reassign_billing",
              t("billing_reviews.reassign"),
              options(
                "billing-assignee-id",
                t("job_board.billing_assignee"),
                Object.fromEntries(
                  billingStaff.map((person) => [person.id, person.name]),
                ),
              ),
            )}
        </CardContent>
      </Card>
    );
  return (
    <Card
      id="job-progress"
      data-job-state={job.work_state}
      data-capacity-state={job.capacity.state}
    >
      <CardHeader>
        <CardTitle>{t("jobs.progress")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <FormErrors
          errors={errors || {}}
          targets={{ cancellation_reason: "cancellation-reason" }}
        />
        <dl className="grid gap-4 sm:grid-cols-3">
          <div>
            <dt className="text-muted-foreground text-sm">{t("jobs.work")}</dt>
            <dd className="font-medium">
              {t(`job_board.stages.${job.work_state}`)}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-sm">
              {t("jobs.payment")}
            </dt>
            <dd>
              {t("job_board.received", {
                amount: money(
                  job.payment_received_minor,
                  total.currency,
                  i18n.locale,
                ),
              })}
            </dd>
            <dd>
              {t("job_board.balance", {
                amount: money(
                  job.outstanding_minor,
                  total.currency,
                  i18n.locale,
                ),
              })}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-sm">
              {t("jobs.collection")}
            </dt>
            <dd>
              {job.collected_at
                ? t("job_board.collected")
                : t("jobs.not_collected")}
            </dd>
          </div>
        </dl>
        <p className="text-sm">
          {t("billing_reviews.current_total", {
            amount: money(job.billing_total_minor, total.currency, i18n.locale),
          })}
        </p>
        <dl className="space-y-2 text-sm">
          <div className="flex flex-wrap justify-between gap-2">
            <dt className="text-muted-foreground">
              {t("job_board.physical_bay")}
            </dt>
            <dd>{t(`job_board.capacity_states.${job.capacity.state}`)}</dd>
          </div>
          {job.capacity.started_at && (
            <div className="flex flex-wrap justify-between gap-2">
              <dt className="text-muted-foreground">
                {t("job_board.capacity_started_at")}
              </dt>
              <dd>
                {dateTime(job.capacity.started_at, job.time_zone, i18n.locale)}
              </dd>
            </div>
          )}
          {job.capacity.released_at && (
            <div className="flex flex-wrap justify-between gap-2">
              <dt className="text-muted-foreground">
                {t("job_board.capacity_released_at")}
              </dt>
              <dd>
                {dateTime(job.capacity.released_at, job.time_zone, i18n.locale)}
              </dd>
            </div>
          )}
        </dl>
        {billingAssigneeName && (
          <div>
            <p className="text-muted-foreground text-sm">
              {t("job_board.billing_assignee")}
            </p>
            <p>{billingAssigneeName}</p>
          </div>
        )}
        <dl className="space-y-2 text-sm">
          {(
            [
              "arrived_at",
              "completed_at",
              "collected_at",
              "done_at",
              "cancelled_at",
            ] as const
          ).map(
            (field) =>
              job[field] && (
                <div
                  key={field}
                  className="flex flex-wrap justify-between gap-2"
                >
                  <dt className="text-muted-foreground">
                    {t(`job_board.${field}`)}
                  </dt>
                  <dd>{dateTime(job[field], job.time_zone, i18n.locale)}</dd>
                </div>
              ),
          )}
        </dl>
        {job.cancellation_reason && (
          <p className="whitespace-pre-wrap break-words">
            {job.cancellation_reason}
          </p>
        )}
        {job.completed_at && !job.collected_at && (
          <p className="text-muted-foreground text-sm">
            {t("job_board.collection_help")}
          </p>
        )}
        {["to_do", "in_progress", "ready_for_collection", "billing"].includes(
          job.work_state,
        ) && (
          <details className="rounded-lg border p-4">
            <summary className="cursor-pointer font-medium text-destructive">
              {t("job_board.cancel_title")}
            </summary>
            <div className="mt-4 space-y-4">
              <p className="text-muted-foreground text-sm">
                {t("job_board.cancel_help")}
              </p>
              {form(
                "cancel",
                t("job_board.cancel"),
                <>
                  {options("no-show", t("job_board.cancel_type"), {
                    false: t("job_board.stages.cancelled"),
                    ...(job.work_state === "to_do"
                      ? { true: t("job_board.stages.no_show") }
                      : {}),
                  })}
                  <div className="space-y-2">
                    <Label htmlFor="cancellation-reason">
                      {t("job_board.reason")}
                    </Label>
                    <Textarea
                      id="cancellation-reason"
                      name="progress[reason]"
                      required
                      maxLength={500}
                      aria-invalid={!!errors.cancellation_reason}
                    />
                    <InputError message={errors.cancellation_reason} />
                  </div>
                </>,
              )}
            </div>
          </details>
        )}
        <Link
          href={locationJobActivityPath(locationId, job.id)}
          className="block text-sm underline"
        >
          {t("job_board.events")}
        </Link>
      </CardContent>
    </Card>
  );
}
